import { isSupabaseConfigured } from '@/lib/env.ts'

/**
 * Where the application reads its content from, and what it is allowed to do
 * when that source is unavailable.
 *
 * The distinction that matters is **live versus everything else**:
 *
 * - `development` — no backend is configured at all. Local fixtures are used
 *   and every screen says so. This is a real, honest mode, not a broken one.
 * - `preview` — a backend is configured and the studio is not verified yet.
 *   Data still comes from the database; the label says the content is a
 *   sample and notifications are suppressed.
 * - `live` — a real studio. **There is no fallback.** If the backend is
 *   unavailable the interface shows an error, never fixtures. A customer must
 *   not be shown invented services, invented free time or an invented booking,
 *   and an owner must not be shown an invented schedule.
 *
 * `development` is decided at build time from the environment. `preview` and
 * `live` come from the tenant record, so they are only known once the backend
 * has answered — which is the point: a live studio cannot degrade into demo
 * content, because reaching demo content requires there to be no backend.
 */
export type RuntimeMode = 'development' | 'preview' | 'live'

/** True when no backend is configured; the only state fixtures are allowed in. */
export const isDevelopmentMode = !isSupabaseConfigured

/**
 * Kept for call sites that only need to know whether fixtures are in play.
 * It is deliberately NOT a synonym for "preview": a preview studio with a
 * backend reads from the database like any other.
 */
export const isDemoDataSource = isDevelopmentMode

export type DataSource = 'supabase' | 'demo'

export const dataSource: DataSource = isSupabaseConfigured ? 'supabase' : 'demo'

/**
 * Whether fixtures may stand in for this studio's content.
 *
 * Only when there is no backend. A live studio is never allowed to, and a
 * preview studio does not need to.
 */
export function mayUseFixtures(mode: RuntimeMode): boolean {
  return mode === 'development'
}

/** Whether the interface should say the content is illustrative. */
export function shouldLabelAsDemo(mode: RuntimeMode, tenantIsDemoContent: boolean): boolean {
  return mode === 'development' || (mode === 'preview' && tenantIsDemoContent)
}

/** Preview studios never send a real notification to anyone. */
export function mayDeliverNotifications(mode: RuntimeMode): boolean {
  return mode === 'live'
}
